import React, { createContext, useContext, useMemo } from 'react';
import { selectedInstrumentCount } from './filters';
import { useCatalogue } from './useCatalogue';

const SessionCatalogueContext = createContext<{
  sessionId?: number;
  catalogue: ReturnType<typeof useCatalogue>;
} | null>(null);

/** One settings-page read supplies both the always-visible rail and the picker. */
export function SessionCatalogueProvider({
  sessionId,
  selectedIds,
  children,
}: {
  sessionId?: number;
  selectedIds?: number[];
  children: React.ReactNode;
}) {
  const catalogue = useCatalogue('selection', sessionId, selectedIds);
  return (
    <SessionCatalogueContext.Provider value={{ sessionId, catalogue }}>
      {children}
    </SessionCatalogueContext.Provider>
  );
}

export function useSessionCatalogue(
  sessionId?: number,
  selectedIds?: number[],
) {
  const shared = useContext(SessionCatalogueContext);
  const matches = shared && shared.sessionId === sessionId;
  // Standalone selectors retain their own read and scope. A picker under the
  // settings shell reuses its request, including reload/error state.
  const local = useCatalogue('selection', sessionId, selectedIds, !matches);
  return matches ? shared.catalogue : local;
}

export function useSelectedInstrumentCount(selectedIds: readonly number[]) {
  const shared = useContext(SessionCatalogueContext);
  const data = shared?.catalogue.data;
  const count = useMemo(
    () => selectedInstrumentCount(selectedIds, data?.assets ?? []),
    [selectedIds, data],
  );
  return !data && shared?.catalogue.loading && selectedIds.length ? '…' : count;
}
